/**
 * GSplat Shader Chunks 注册中心
 * 对标 PlayCanvas gsplat-chunks-glsl.js
 *
 * 命名规范：
 * - 顶点着色器 chunks: {功能名}VS (如 gsplatStructsVS)
 * - 片段着色器 chunks: {功能名}PS (如 gsplatPS)
 */

import { ShaderChunkManager } from './ShaderChunkManager'

// ============================================================
// 顶点着色器 chunks (vert/)
// ============================================================

import gsplatVS from './chunks/vert/gsplat.glsl'
// 核心变换
import gsplatCenterVS from './chunks/vert/gsplatCenter.glsl'
import gsplatClipVS from './chunks/vert/gsplatClip.glsl'
import gsplatColorVS from './chunks/vert/gsplatColor.glsl'
// 公共入口和主入口
import gsplatCommonVS from './chunks/vert/gsplatCommon.glsl'
// 压缩格式
import gsplatCompressedDataVS from './chunks/vert/gsplatCompressedData.glsl'
import gsplatCompressedSHVS from './chunks/vert/gsplatCompressedSH.glsl'
import gsplatCornerVS from './chunks/vert/gsplatCorner.glsl'
import gsplatCovarianceVS from './chunks/vert/gsplatCovariance.glsl'
import gsplatCustomizeVS from './chunks/vert/gsplatCustomize.glsl'
// 标准 PLY 格式
import gsplatDataVS from './chunks/vert/gsplatData.glsl'
import gsplatEvalSHVS from './chunks/vert/gsplatEvalSH.glsl'
import gsplatEvalSHArrayVS from './chunks/vert/gsplatEvalSHArray.glsl'
import gsplatHelpersVS from './chunks/vert/gsplatHelpers.glsl'
import gsplatHighQualitySHVS from './chunks/vert/gsplatHighQualitySH.glsl'
// 输出处理
import gsplatOutputVS from './chunks/vert/gsplatOutput.glsl'
import gsplatQuatToMat3VS from './chunks/vert/gsplatQuatToMat3.glsl'
// SH 相关
import gsplatSHVS from './chunks/vert/gsplatSH.glsl'
import gsplatSHCoeffsVS from './chunks/vert/gsplatSHCoeffs.glsl'
import gsplatSHConstantsVS from './chunks/vert/gsplatSHConstants.glsl'
import gsplatSogsColorVS from './chunks/vert/gsplatSogsColor.glsl'
// SOGS 格式
import gsplatSogsDataVS from './chunks/vert/gsplatSogsData.glsl'
import gsplatSogsSHVS from './chunks/vert/gsplatSogsSH.glsl'
// 数据源
import gsplatSourceVS from './chunks/vert/gsplatSource.glsl'
import gsplatSourceFormatVS from './chunks/vert/gsplatSourceFormat.glsl'
// 基础结构
import gsplatStructsVS from './chunks/vert/gsplatStructs.glsl'
// WorkBuffer 格式
import gsplatWorkBufferVS from './chunks/vert/gsplatWorkBuffer.glsl'

// ============================================================
// 片段着色器 chunks (frag/)
// ============================================================

import gsplatPS from './chunks/frag/gsplat.glsl'
import gsplatSogsReorderCommonPS from './chunks/frag/gsplatSogsReorderCommon.glsl'
// 共享 packing 真源，同时供 chunk 系统和手工替换路径复用。
import gsplatPackingPS from './chunks/gsplatPacking.glsl'
import { DEFAULT_GSPLAT_MODIFY_FRAGMENT_CHUNK } from './gsplatModifyChunk'

/**
 * 所有 GSplat chunks 集合
 */
export const gsplatChunks = {
  // 共享 chunk 别名（兼容不同 include 风格）
  gsplatPackingPS,
  gsplatPacking: gsplatPackingPS,
  gsplatSogsReorderCommonPS,

  // 顶点着色器 - 基础结构
  gsplatStructsVS,
  gsplatHelpersVS,
  gsplatHighQualitySHVS,
  gsplatQuatToMat3VS,
  // PlayCanvas 风格入口名。旧文件名仍为 gsplatCustomize.glsl，便于兼容现有内部 hook。
  gsplatModifyVS: gsplatCustomizeVS,
  gsplatCustomizeVS,
  gsplatCovarianceVS,
  gsplatClipVS,

  // 顶点着色器 - 输出处理
  gsplatOutputVS,

  // 顶点着色器 - 数据源
  gsplatSourceVS,
  gsplatSourceFormatVS,

  // 顶点着色器 - 标准 PLY 格式
  gsplatDataVS,
  gsplatColorVS,

  // 顶点着色器 - SOGS 格式
  gsplatSogsDataVS,
  gsplatSogsColorVS,
  gsplatSogsSHVS,

  // 顶点着色器 - WorkBuffer 格式
  gsplatWorkBufferVS,

  // 顶点着色器 - 核心变换
  gsplatCenterVS,
  gsplatCornerVS,

  // 顶点着色器 - SH 相关
  gsplatSHVS,
  gsplatSHCoeffsVS,
  gsplatSHConstantsVS,
  gsplatEvalSHVS,
  gsplatEvalSHArrayVS,
  gsplatCompressedDataVS,
  gsplatCompressedSHVS,

  // 顶点着色器 - 入口
  gsplatCommonVS,
  gsplatVS,

  // 片段着色器
  gsplatModifyPS: DEFAULT_GSPLAT_MODIFY_FRAGMENT_CHUNK,
  gsplatPS,
}

// 单例缓存
let _managerInstance: ShaderChunkManager | null = null
let _isRegistered = false

/**
 * 初始化并注册所有 chunks 到 ShaderChunkManager。
 *
 * @returns 注册完成后的 ShaderChunkManager
 */
export function registerGSplatChunks(): ShaderChunkManager {
  if (!_managerInstance) {
    _managerInstance = ShaderChunkManager.getInstance()
  }

  if (!_isRegistered) {
    _managerInstance.registerAll(gsplatChunks)
    _isRegistered = true
  }

  return _managerInstance
}

/**
 * 获取已注册的 ShaderChunkManager 实例，首次调用会触发注册。
 *
 * @returns 注册完成后的 ShaderChunkManager
 */
export function getGSplatShaderManager(): ShaderChunkManager {
  if (!_isRegistered) {
    return registerGSplatChunks()
  }
  return _managerInstance!
}

/** gsplat shader chunk 名称字面量联合类型 */
export type GSplatChunkName = keyof typeof gsplatChunks

export type GSplatShaderDefines = Record<string, string | number | boolean>

export type GSplatChunkOverrides = Partial<Record<GSplatChunkName, string>> &
  Record<string, string | undefined>

export interface GSplatShaderAssemblyOptions {
  defines?: GSplatShaderDefines
  chunkOverrides?: GSplatChunkOverrides
}

function isAssemblyOptions(
  value?: GSplatShaderDefines | GSplatShaderAssemblyOptions
): value is GSplatShaderAssemblyOptions {
  return !!value && ('defines' in value || 'chunkOverrides' in value)
}

/**
 * 使用 chunk 系统组装着色器
 * @param optionsOrDefines 预处理定义，或包含 defines / chunkOverrides 的组装选项
 * @returns { vertexShader, fragmentShader }
 */
export function assembleGSplatShaders(
  optionsOrDefines?: GSplatShaderDefines | GSplatShaderAssemblyOptions
): {
  vertexShader: string
  fragmentShader: string
} {
  const manager = getGSplatShaderManager()
  const defines = isAssemblyOptions(optionsOrDefines) ? optionsOrDefines.defines : optionsOrDefines
  const chunkOverrides = isAssemblyOptions(optionsOrDefines)
    ? optionsOrDefines.chunkOverrides
    : undefined

  return {
    vertexShader: manager.assemble('gsplatVS', defines, chunkOverrides),
    fragmentShader: manager.assemble('gsplatPS', defines, chunkOverrides),
  }
}

// 导出 ShaderChunkManager 供外部使用
export { ShaderChunkManager }
